import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useCollections } from '@/hooks/useCollections'
import { useQueryCollectionItems } from '@/hooks/useQueryCollectionItems'
import { useQueryTattoosByCollectionSimilarity } from '@/hooks/useQueryTattoosByCollectionSimilarity'
import { useSaves } from '@/contexts/SavesContext'
import { useAuthState } from '@/hooks/useAuth'
import { saveItemToCollections } from '@/lib/savesApi'
import { Button } from '@/components/ui/button'
import { ArrowLeft, Sparkles, X } from 'lucide-react'
import { FlashCard } from '@/components/shared/tattoo/FlashCard'
import { TattooCard } from '@/components/shared/tattoo/TattooCard'
import { BaseMasonryGrid } from '@/components/shared/BaseMasonryGrid'
import { useArtists } from '@/hooks/useArtists'
import type { Collection, PortfolioItem } from '@/types/models'
import { useAnalytics } from '@/hooks/useAnalytics'

export default function CollectionDetailPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { collectionId } = useParams<{ collectionId: string }>()
  const { getCollection } = useCollections()
  
  const [collection, setCollection] = useState<Collection | null>(null)
  const [collectionLoadError, setCollectionLoadError] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<'collection' | 'similar'>('collection')
  const scrollPositionRef = useRef<number>(0)
  
  const { user } = useAuthState()
  const { refresh: refreshSaves } = useSaves()
  const { trackEvent } = useAnalytics()

  const {
    items: collectionItems,
    loading: itemsLoading,
    loadingMore,
    hasMore,
    error: itemsError,
    loadMoreItems,
    initialLoadComplete,
    refetch: refetchCollectionItems
  } = useQueryCollectionItems(collectionId)

  // Fetch similar items when in similar view mode
  const similarItemsFilters = viewMode === 'similar' && collectionId ? {
    collectionId: collectionId,
    collections: ['flashes' as const, 'portfolioItems' as const],
    includeBooked: false,
  } : undefined

  const {
    items: similarItems,
    loading: similarItemsLoading,
    loadingMore: similarItemsLoadingMore,
    hasMore: similarItemsHasMore,
    loadMoreItems: loadMoreSimilarItems,
    initialLoadComplete: similarItemsInitialLoadComplete
  } = useQueryTattoosByCollectionSimilarity(similarItemsFilters)

  // Determine which items to show based on view mode
  const displayedItems = viewMode === 'similar' ? similarItems : collectionItems
  const displayedLoading = viewMode === 'similar' ? similarItemsLoading : itemsLoading
  const displayedLoadingMore = viewMode === 'similar' ? similarItemsLoadingMore : loadingMore
  const displayedHasMore = viewMode === 'similar' ? similarItemsHasMore : hasMore
  const displayedLoadMoreItems = viewMode === 'similar' ? loadMoreSimilarItems : loadMoreItems
  const displayedInitialLoadComplete = viewMode === 'similar' ? similarItemsInitialLoadComplete : initialLoadComplete

  // Get unique artist IDs from displayed items
  const artistIds = Array.from(new Set(displayedItems.map(item => item.artistId)))
  const { artists } = useArtists(artistIds)

  useEffect(() => {
    async function fetchCollectionMetadata() {
      if (!collectionId) return

      try {
        const collectionData = await getCollection(collectionId)
        if (!collectionData) {
          setCollectionLoadError('Collection not found')
          return
        }
        
        setCollection(collectionData)
      } catch (err) {
        console.error('Error fetching collection metadata:', err)
        setCollectionLoadError('Failed to load collection')
      }
    }

    fetchCollectionMetadata()
  }, [collectionId, getCollection])

  // Restore scroll position when coming back from a detail page
  useEffect(() => {
    const restoreScroll = () => {
      const locationState = location.state as { scrollPosition?: number } | null
      const savedPositionFromLocation = locationState?.scrollPosition
      const savedPositionFromHistory = window.history.state?.usr?.scrollPosition
      const savedPosition =
        typeof savedPositionFromLocation === 'number'
          ? savedPositionFromLocation
          : typeof savedPositionFromHistory === 'number'
            ? savedPositionFromHistory
            : undefined

      if (typeof savedPosition === 'number') {
        window.scrollTo(0, Math.max(0, savedPosition))
      } else if (scrollPositionRef.current > 0) {
        window.scrollTo(0, scrollPositionRef.current)
      }
    }

    // Small delay to ensure content is rendered
    const timeoutId = setTimeout(restoreScroll, 100)
    return () => clearTimeout(timeoutId)
  }, [location.key, location.state])

  // Save scroll position before navigating away
  useEffect(() => {
    const handleScroll = () => {
      scrollPositionRef.current = window.scrollY
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Get unique artists for the loaded items
  const artistsMap = new Map(artists.map(artist => [artist.id, artist]))

  // Handle adding item to collection from similar items view
  const handleAddToCollection = useCallback(async (itemId: string) => {
    if (!collectionId || !user) return
    
    try {
      // Get the item to check if it's a flash
      const itemToAdd = similarItems.find(item => item.id === itemId)
      if (!itemToAdd) return
      
      // Add the item to this collection
      await saveItemToCollections(
        itemId,
        user.uid,
        Boolean(itemToAdd.isFlash),
        [collectionId]
      )

      trackEvent('collection_similar_added', {
        collection_id: collectionId,
        item_id: itemToAdd.id,
        item_type: itemToAdd.isFlash ? 'flash' : 'tattoo'
      })
      
      // Refresh saves context
      await refreshSaves()
    } catch (error) {
      console.error('Error adding item to collection:', error)
    }
  }, [collectionId, user, similarItems, refreshSaves, trackEvent])

  const renderItem = useCallback((item: PortfolioItem, artist?: any) => {
    const itemArtist = artist || artistsMap.get(item.artistId)
    const isInSimilarView = viewMode === 'similar'
    
    if (item.isFlash) {
      return (
        <FlashCard
          key={item.id}
          item={item}
          artist={itemArtist}
          showAddToCollection={isInSimilarView}
          onAddToCollection={isInSimilarView ? handleAddToCollection : undefined}
        />
      )
    }
    
    return (
      <TattooCard
        key={item.id}
        item={item}
        artist={itemArtist}
        showAddToCollection={isInSimilarView}
        onAddToCollection={isInSimilarView ? handleAddToCollection : undefined}
      />
    )
  }, [artistsMap, viewMode, handleAddToCollection])

  const handleToggleSimilar = useCallback(() => {
    const newMode = viewMode === 'collection' ? 'similar' : 'collection'
    setViewMode(newMode)

    if (newMode === 'similar' && collectionId) {
      trackEvent('collection_find_similar_clicked', { collection_id: collectionId })
    }
    
    // If going back to collection view, refresh the items to show newly added ones
    if (newMode === 'collection') {
      refetchCollectionItems()
    }
    
    // Scroll to top when switching views
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [viewMode, refetchCollectionItems, trackEvent, collectionId])

  return (
    <div className="min-h-screen bg-background pb-bottom-nav">
      <div className="sticky-header-notch-backdrop sticky top-0 shrink-0 md:top-[52px] z-40 bg-background/95 backdrop-blur-md border-b border-border/50 dark:border-border/30 rounded-b-2xl">
        <div className="phone-container-padded pt-0 pb-3">
          <div className="flex items-center mb-2">
            <Button 
              variant="ghost" 
              size="icon" 
              className="mr-2"
              onClick={() => navigate('/profile')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold truncate">
                {collection?.title || t('profile.collection')}
              </h1>
              {collection && viewMode === 'collection' && (
                <p className="text-xs text-muted-foreground">
                  {t('profile.itemCount', { count: collection.itemCount || 0 })}
                </p>
              )}
              {viewMode === 'similar' && (
                <p className="text-xs text-muted-foreground">
                  {t('collection.similarItems')}
                </p>
              )}
            </div>
          </div>
          
          {/* Toggle button for similar items */}
          {collectionItems.length > 0 && (
            <Button
              onClick={handleToggleSimilar}
              variant="default"
              size="sm"
              className="w-full rounded-full py-5"
            >
              {viewMode === 'similar' ? (
                <>
                  <X className="w-4 h-4 mr-2" />
                  {t('collection.backToCollection', 'Back to Collection')}
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  {t('collection.findSimilar')}
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      <div className="relative z-0 phone-container-padded pb-20">
        {collectionLoadError ? (
          <div className="text-center py-8">
            <p className="text-destructive">{collectionLoadError}</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => navigate('/profile')}
            >
              {t('common.back')}
            </Button>
          </div>
        ) : itemsLoading && !initialLoadComplete ? (
          <div className="text-center py-8">
            <p>{t('common.loading')}</p>
          </div>
        ) : itemsError ? (
          <div className="text-center py-8">
            <p className="text-destructive">Failed to load collection items</p>
          </div>
        ) : collectionItems.length === 0 && viewMode === 'collection' ? (
          <div className="text-center py-8 border border-dashed rounded-lg mt-4">
            <p className="text-muted-foreground">{t('profile.emptyCollection')}</p>
          </div>
        ) : viewMode === 'similar' && similarItemsLoading && !similarItemsInitialLoadComplete ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">{t('common.loading')}</p>
          </div>
        ) : viewMode === 'similar' && similarItems.length === 0 && similarItemsInitialLoadComplete ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">{t('collection.noSimilarItems', 'No similar items found')}</p>
          </div>
        ) : (
          <div className="py-3">
            <BaseMasonryGrid
              items={displayedItems}
              renderItem={renderItem}
              loadMoreItems={displayedLoadMoreItems}
              hasMore={displayedHasMore}
              loading={displayedLoading}
              loadingMore={displayedLoadingMore}
              artists={artists}
              initialLoadComplete={displayedInitialLoadComplete}
            />
          </div>
        )}
      </div>
    </div>
  )
}
